import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { CircularProgress } from '@material-ui/core/';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components/macro';
import { BackTo, showToast, Typography, NoData } from '@deeptrue-frontend/ui';
import { OrganizationType } from 'store/modules/auth/auth';
import { Dashboard } from 'components/Layout/';
import { useFormatMessage } from 'components/IntlProvider';
import Report from './Report';
import Legend from './Legend';
import ProjectMenu from '../../Common/ProjectMenu';
import { WithProjectProps } from '../../withProject';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import api from 'api';
import WaitingPlaceholder from 'components/WaitingPlaceholder/WaitingPlaceholder';
import IndividualReportsTable, {
  RowData,
} from './IndividualResponses/IndividualReportsTable';
import generateReportData from './reportGenerator';
import PaymentsBarReminder from 'components/Project/Subscriptions/PaymentBarReminder';

const { connect, selectors } = StoreProvider;

interface ProjectProps
  extends RouteComponentProps<{ id: string; codename: string }> {
  product: WithProjectProps['product'];
  project: WithProjectProps['project'];
  organization: OrganizationType;
  isBexioUser: boolean;
}
const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10rem;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 5rem;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;

  & > div:first-of-type {
    position: relative;
    padding-right: 2rem;
    margin-bottom: 4rem;
    border-right: 1px solid ${({ theme }) => theme.colors.quillGray};
    width: 30rem;
  }
`;

const SatisfactionNoteDemoSet = styled.div`
  width: 100%;
`;

const ProjectMenuWrapper = styled.div`
  width: 100%;
`;

const ReportTitle = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 500;
  padding-left: 2rem;
`;

const ReportTitleBox = styled.div`
  display: flex;
  align-items: center;
  height: 5rem;
  border-bottom: 1px solid #e6e6e5;
`;

const ReportContainer = styled.div`
  width: 100%;
`;

const CustomerSatisfaction = ({
  location,
  history,
  match: { params },
  project,
  organization,
  isBexioUser,
}: any) => {
  const [statistic, setStatistic] = useState({
    untouched: null,
    started: null,
    screenout: null,
    quotafull: null,
    completed: null,
    total_completed: null,
    quantiles: {
      Q1: null,
      Q2: null,
      Q3: null,
    },
  });
  const [reports, setReports] = useState([]);
  const [individualReports, setIndividualReports] = useState<RowData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reportType, setReportType] = useState<'1' | '2'>('1');
  const { locale } = useIntl();
  const t = useFormatMessage();

  const { surveyUuid, subscription, is_subscribable } = project;

  useEffect(() => {
    if (project.id !== -1) {
      api.resources
        .getSurveysStatistics({
          orgName: organization,
          uuid: project.uuid,
        })
        .then(({ data }) => {
          setStatistic(data);
        })
        .catch(() => {
          showToast({
            type: 'error',
            title: t(
              'errors.toast.general',
              'Something went wrong, please contact administrator'
            ),
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  useEffect(() => {
    if (surveyUuid) {
      Promise.all([
        api.resources.getReport({
          organizationCodename: organization,
          surveyUuid,
        }),
        api.resources.getAllReports({
          organizationCodename: organization,
          surveyUuid,
        }),
      ])
        .then(([aggregatedReport, individualReports]) => {
          setReports(aggregatedReport.data);
          setIndividualReports(individualReports.data);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, [surveyUuid]);

  const areReportsExist = reports.length > 0;

  const dataToSet = areReportsExist
    ? generateReportData(reports, locale as any)
    : [];

  const isSummarizedReportChoosen = reportType === '1';

  const shouldDisplayCardReminder = is_subscribable ? !subscription : false;
  const overviewUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/`
    : `/`;
  return (
    <Dashboard
      title={project.name || t('page.project', 'Project details')}
      location={location}
      isFullWidth
      combinedTitle={project.combinedTitle}
    >
      {!isBexioUser && (
        <BackTo onClick={() => history.push(overviewUrl)}>
          {t('project.backTo.overview', 'Back to overview')}
        </BackTo>
      )}
      <ProjectMenuWrapper>
        <ProjectMenu
          id={params.id}
          projectStatus={project.status}
          isProjectSubscribable={is_subscribable}
          isProjectLoaded={project.id !== -1}
        />
      </ProjectMenuWrapper>
      {shouldDisplayCardReminder && <PaymentsBarReminder />}
      {isLoading ? (
        <LoaderWrapper>
          <CircularProgress size={80} color="primary" />
        </LoaderWrapper>
      ) : areReportsExist ? (
        <Container>
          <Legend
            totalCompleted={statistic.total_completed}
            dataToSet={dataToSet}
            reportType={reportType}
            setReportType={setReportType}
          />

          <SatisfactionNoteDemoSet>
            <ReportTitleBox>
              <ReportTitle>
                {isSummarizedReportChoosen
                  ? t('customerSatisfaction.title', 'Summarized Report')
                  : t(
                      'customerSatisfaction.reportType.individual',
                      'Individual Report'
                    )}
              </ReportTitle>
            </ReportTitleBox>
            <ReportContainer>
              {isSummarizedReportChoosen ? (
                <Report reportData={dataToSet} />
              ) : (
                <IndividualReportsTable
                  rows={individualReports}
                  surveyUuid={surveyUuid}
                  organization={organization}
                />
              )}
            </ReportContainer>
          </SatisfactionNoteDemoSet>
        </Container>
      ) : !isBexioUser ? (
        <Wrapper>
          <NoData
            title={t(
              'project.statistic.bexio.nodata.report.title',
              'Waiting for responses'
            )}
            description={t(
              'project.statistic.nodata.report.description',
              'We will now automatically send the survey to your audience. As soon as the first responses are available, the report will be generated.'
            )}
          />
        </Wrapper>
      ) : (
        <LoaderWrapper>
          <WaitingPlaceholder />
        </LoaderWrapper>
      )}
    </Dashboard>
  );
};

export default connect((state) => ({
  isBexioUser: selectors.authSelectors.isBexioUser(state),
}))(CustomerSatisfaction);
